/**
 * Auth Hook
 * 인증 관련 로직 관리 (로그인, 회원가입, 협력사 확인 등)
 *
 * @description
 * 로그인, 회원가입, 로그아웃 등 인증 관련 기능 제공
 * useAsync hook을 활용한 비동기 상태 관리
 */
import { useCallback, useMemo} from 'react';
import { useAsync } from '@/shared/hooks/useAsync';
import { setItem, getItem, removeItem, STORAGE_KEYS } from '@/shared/utils/storage';
import { authApi } from '../api/authApi';
import {
  PartnerPortalLoginRequest,
  PartnerPortalLoginResponse,
  CheckSupplierIdRequest,
  HeadquartersRegisterRequest,
  SupplierRegisterRequest,
} from '../types/auth.types';

// ============================================================
// 상수 정의
// ============================================================

/** 스토리지에 저장되는 사용자 정보 키 목록 **/
const USER_STORAGE_KEYS = [
    'USER_ID',
    'USER_NAME',
    'USER_TYPE',
    'PHONE_NUM',
    'ADMIN_YN',
    'SUPP_TYPE',
    'PRM_CODE',
    'ACCESS_PAGES',
] as const;

// ============================================================
// Hook 구현
// ============================================================

/**
 * 인증 Hook
 * @returns 인증 관련 상태 및 액션
 */
export const useAuth = () => {
    // 각 API 작업별 비동기 상태 관리
    const loginState = useAsync(authApi.login);
    const checkSupplierIdState = useAsync(authApi.checkSupplierId);
    const registerHeadquartersState = useAsync(authApi.registerHeadquarters);
    const registerSupplierState = useAsync(authApi.registerSupplier);

    /**
     * suppType 추론 (백엔드에서 null일 경우 대비)
     * @param response - 로그인 응답
     * @returns 추론된 suppType
     */
    const inferSuppType = useCallback((response: PartnerPortalLoginResponse): string | undefined => {
        if (response.suppType) return response.suppType;

        // SUPPLIER 유형인데 suppType이 없으면 userId 기반 추론

        if (response.userType === 'SUPPLIER') {
            const userId = response.userId?.toLowerCase() ?? '';
            return userId.includes('가맹') || userId.includes('franchise') ? 'F' : 'D';
        }
    }, []);

    /**
    * 인증 정보 저장
    * @param response - 로그인 응답 데이터
    * @param rememberMe - 로그인 상태 유지 여부
    */
    const saveAuth = useCallback((response: PartnerPortalLoginResponse, rememberMe = false) => {
        // 토큰 및 세션 정보 저장
        setItem(STORAGE_KEYS.ACCESS_TOKEN, response.accessToken);
        setItem(STORAGE_KEYS.SESSION_ID, response.refreshToken);
        setItem(STORAGE_KEYS.USER_INFO, response);

        // 사용자 정보 저장
        setItem('USER_ID', response.userId);
        setItem('USER_NAME', response.userName);
        setItem('USER_TYPE', response.userType);
        setItem('PHONE_NUM', response.phoneNum);

        // 선택적 필드 저장
        if (response.adminYn !== undefined) {
            setItem('ADMIN_YN', response.adminYn);
        }

        const suppType = inferSuppType(response);
        if (suppType) {
            setItem('SUPP_TYPE', suppType);
        }

        // 권한 코드 저장
        if (response.prmCode) {
            setItem('PRM_CODE', response.prmCode);
        }

        // 접근 페이지 저장 (커스텀 권한의 경우)
        if (response.accessPages) {
            setItem('ACCESS_PAGES', response.accessPages);
        } else {
            removeItem('ACCESS_PAGES');
        }

        // 로그인 상태 유지 설정
        if (rememberMe) {
            setItem(STORAGE_KEYS.REMEMBER_ME, true);
        } else {
            removeItem(STORAGE_KEYS.REMEMBER_ME);
        }
    }, [inferSuppType]);

    /**
    * 인증 정보 삭제
    */
    const clearAuth = useCallback(() => {
        // 토큰 정보 삭제
        removeItem(STORAGE_KEYS.ACCESS_TOKEN);
        removeItem(STORAGE_KEYS.SESSION_ID);
        removeItem(STORAGE_KEYS.USER_INFO);
        removeItem(STORAGE_KEYS.REMEMBER_ME);

        // 사용자 정보 삭제
        USER_STORAGE_KEYS.forEach(key => removeItem(key));
    }, []);

    /**
    * 로그인 실행
    * @param userId - 사용자 아이디
    * @param password - 비밀번호
    * @param rememberMe - 로그인 상태 유지 여부
    * @returns 로그인 응답
    */
    const login = useCallback(
        async (userId: string, password: string, rememberMe = false) => {
            const request: PartnerPortalLoginRequest = { userId, password };
            const response = await loginState.execute(request);

            if (response.success) {
                saveAuth(response, rememberMe);
            }

            return response;
        },
        [loginState, saveAuth]
    );

    /**
    * 협력사 ID 확인
    * @param supplierId - 협력사 ID
    * @returns 협력사 정보 응답
    */
    const checkSupplierId = useCallback(
        async (supplierId: string) => {
            const request: CheckSupplierIdRequest = { supplierId };
            return checkSupplierIdState.execute(request);
        },
        [checkSupplierIdState]
    );

    /**
    * 본사 관리자 회원가입
    * @param request - 본사 회원가입 요청 데이터
    * @returns 회원가입 응답
    */
    const registerHeadquarters = useCallback(
        (request: HeadquartersRegisterRequest) => registerHeadquartersState.execute(request),
        [registerHeadquartersState]
    );

    /**
    * 협력사 회원가입
    * @param request - 협력사 회원가입 요청 데이터
    * @returns 회원가입 응답
    */
    const registerSupplier = useCallback(
        (request: SupplierRegisterRequest) => registerSupplierState.execute(request),
        [registerSupplierState]
    );

    /**
    * 로그아웃
    */
    const logout = useCallback(() => {
        clearAuth();
        loginState.reset();
        checkSupplierIdState.reset();
        registerHeadquartersState.reset();
        registerSupplierState.reset();
    }, [clearAuth, loginState, checkSupplierIdState, registerHeadquartersState, registerSupplierState]);

    /**
    * 저장된 사용자 정보 조회
    * @returns 사용자 정보 객체
    */
    const getUserInfo = useCallback(() => ({
        userId: getItem<string>('USER_ID'),
        userName: getItem<string>('USER_NAME'),
        userType: getItem<string>('USER_TYPE'),
        accessToken: getItem<string>(STORAGE_KEYS.ACCESS_TOKEN),
    }), []);

    /**
     * 로딩 상태 계산 (메모이제이션)
     */
    const isLoading = useMemo(
        () =>
            loginState.isLoading ||
            checkSupplierIdState.isLoading ||
            registerHeadquartersState.isLoading ||
            registerSupplierState.isLoading,
        [
            loginState.isLoading,
            checkSupplierIdState.isLoading,
            registerHeadquartersState.isLoading,
            registerSupplierState.isLoading,
        ]
    );

    return {
        // 상태
        loginState: loginState.result,
        checkSupplierIdState: checkSupplierIdState.result,
        registerHeadquartersState: registerHeadquartersState.result,
        registerSupplierState: registerSupplierState.result,

        // 액션
        login,
        checkSupplierId,
        registerHeadquarters,
        registerSupplier,
        logout,
        getUserInfo,

        // 로딩 상태
        isLoading,
    };
};
